import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentTransactions = () => {
  const navigate = useNavigate();
  const { userProfile, isAuthenticated } = useAuth();
  const [filter, setFilter] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Load transactions from multiple sources
  useEffect(() => {
    const loadTransactions = async () => {
      if (!isAuthenticated || !userProfile?.organization_id) {
        setError('No organization found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if supabase client is available
        if (!supabase) {
          throw new Error('Database connection unavailable');
        }

        const orgId = userProfile?.organization_id;
        let allTransactions = [];

        // Fetch from multiple sources with proper error handling
        try {
          // 1. Journal Entries
          const { data: journalData, error: journalError } = await supabase?.from('journal_entries')?.select(`
              id,
              entry_number,
              description,
              entry_date,
              total_debit,
              total_credit,
              status,
              created_at
            `)?.eq('organization_id', orgId)?.order('entry_date', { ascending: false })?.limit(5);

          if (!journalError && journalData?.length > 0) {
            const journalTransactions = journalData?.map(entry => ({
              id: entry?.entry_number || entry?.id,
              type: entry?.total_credit > entry?.total_debit ? 'payment' : 'expense',
              description: entry?.description || 'Journal Entry',
              amount: entry?.total_credit > 0 ? entry?.total_credit : -entry?.total_debit,
              status: entry?.status === 'posted' ? 'completed' : entry?.status,
              date: entry?.entry_date,
              category: entry?.total_credit > entry?.total_debit ? 'Income' : 'Expense',
              source: 'journal_entry',
              source_id: entry?.id
            }));
            allTransactions?.push(...journalTransactions);
          }
        } catch (err) {
          console.warn('Journal entries fetch failed:', err);
        }

        try {
          // 2. Recent Invoices
          const { data: invoiceData, error: invoiceError } = await supabase?.from('invoices')?.select(`
              id,
              invoice_number,
              total_amount,
              amount_paid,
              status,
              issue_date,
              customer_id
            `)?.eq('organization_id', orgId)?.order('issue_date', { ascending: false })?.limit(3);

          if (!invoiceError && invoiceData?.length > 0) {
            const invoiceTransactions = invoiceData?.map(invoice => ({
              id: invoice?.invoice_number || invoice?.id,
              type: 'invoice',
              description: `Invoice ${invoice?.invoice_number}`,
              amount: invoice?.total_amount || 0,
              status: invoice?.status,
              date: invoice?.issue_date,
              category: 'Sales',
              source: 'invoice',
              source_id: invoice?.id
            }));
            allTransactions?.push(...invoiceTransactions);
          }
        } catch (err) {
          console.warn('Invoices fetch failed:', err);
        }

        try {
          // 3. Recent Bills/Expenses
          const { data: billData, error: billError } = await supabase?.from('bills')?.select(`
              id,
              bill_number,
              total_amount,
              status,
              issue_date,
              vendor_id
            `)?.eq('organization_id', orgId)?.order('issue_date', { ascending: false })?.limit(3);

          if (!billError && billData?.length > 0) {
            const billTransactions = billData?.map(bill => ({
              id: bill?.bill_number || bill?.id,
              type: 'expense',
              description: `Bill ${bill?.bill_number}`,
              amount: -(bill?.total_amount || 0),
              status: bill?.status,
              date: bill?.issue_date,
              category: 'Expense',
              source: 'bill',
              source_id: bill?.id
            }));
            allTransactions?.push(...billTransactions);
          }
        } catch (err) {
          console.warn('Bills fetch failed:', err);
        }

        try {
          // 4. Recent Payments
          const { data: paymentData, error: paymentError } = await supabase?.from('payments_received')?.select(`
              id,
              payment_reference,
              amount_received,
              status,
              payment_date,
              description
            `)?.eq('organization_id', orgId)?.order('payment_date', { ascending: false })?.limit(3);

          if (!paymentError && paymentData?.length > 0) {
            const paymentTransactions = paymentData?.map(payment => ({
              id: payment?.payment_reference || payment?.id,
              type: 'payment',
              description: payment?.description || `Payment ${payment?.payment_reference}`,
              amount: payment?.amount_received || 0,
              status: payment?.status,
              date: payment?.payment_date,
              category: 'Income',
              source: 'payment',
              source_id: payment?.id
            }));
            allTransactions?.push(...paymentTransactions);
          }
        } catch (err) {
          console.warn('Payments fetch failed:', err);
        }

        // Sort by date (most recent first) and take top 10
        const sortedTransactions = allTransactions?.sort((a, b) => new Date(b.date) - new Date(a.date))?.slice(0, 10);

        setTransactions(sortedTransactions);

        // Reset retry count on success
        setRetryCount(0);

      } catch (err) {
        console.error('Error loading transactions:', err);
        
        // Determine error type for better user feedback
        let errorMessage = 'Failed to load transactions';
        if (err?.message?.includes('Failed to fetch') || err?.name === 'TypeError') {
          errorMessage = 'Network connection error. Please check your internet connection.';
        } else if (err?.message?.includes('unavailable')) {
          errorMessage = 'Database connection unavailable. Please try again.';
        } else if (err?.code === 'PGRST301') {
          errorMessage = 'Access denied. Please check your permissions.';
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, [isAuthenticated, userProfile?.organization_id, retryCount]);

  // Retry function with exponential backoff
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'invoice': return 'FileText';
      case 'expense': return 'Receipt';
      case 'payment': return 'CreditCard';
      case 'bill': return 'FileX';
      default: return 'DollarSign';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': case'completed': case'posted': 
        return 'text-success bg-success/10';
      case 'pending': case'draft': 
        return 'text-warning bg-warning/10';
      case 'overdue': 
        return 'text-error bg-error/10';
      default: 
        return 'text-muted-foreground bg-muted/10';
    }
  };

  const getAmountColor = (amount) => {
    return amount > 0 ? 'text-success' : 'text-error';
  };

  // Use real transactions or empty array
  const displayTransactions = transactions || [];
  const filteredTransactions = filter === 'all' 
    ? displayTransactions 
    : displayTransactions?.filter(t => t?.type === filter);

  const handleTransactionClick = (transaction) => {
    // Navigate based on source
    switch (transaction?.source) {
      case 'journal_entry': navigate('/accountant-tools');
        break;
      case 'invoice': case'payment': navigate('/sales-hub');
        break;
      case 'bill': navigate('/purchase-center');
        break;
      default:
        navigate('/banking');
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="p-6 text-center">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Loading transactions...</p>
        </div>
      );
    }

    if (error && !userProfile?.organization_id) {
      return (
        <div className="p-6 text-center">
          <Icon name="AlertTriangle" size={24} className="text-warning mx-auto mb-2" />
          <p className="text-warning font-medium mb-2">No Organization Found</p>
          <p className="text-muted-foreground text-sm mb-4">
            You need to be a member of an organization to view transactions.
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/organization-settings')}
          >
            Setup Organization
          </Button>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6 text-center">
          <Icon name="Wifi" size={24} className="text-error mx-auto mb-2" />
          <p className="text-error font-medium mb-2">Connection Error</p>
          <p className="text-muted-foreground text-sm mb-4">{error}</p>
          <div className="flex justify-center space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRetry}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={14} className="animate-spin mr-2" />
                  Retrying...
                </>
              ) : (
                <>
                  <Icon name="RotateCcw" size={14} className="mr-2" />
                  Retry
                </>
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => window?.location?.reload()}
            >
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    if (filteredTransactions?.length === 0) {
      return (
        <div className="p-6 text-center">
          <Icon name="Receipt" size={24} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground font-medium mb-2">No transactions found</p>
          <p className="text-sm text-muted-foreground mb-4">
            Start by creating invoices, recording expenses, or adding journal entries to see recent activity here.
          </p>
          <div className="flex justify-center space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/sales-hub')}
            >
              Create Invoice
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/purchase-center')}
            >
              Record Expense
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="divide-y divide-border">
        {filteredTransactions?.map((transaction) => (
          <div
            key={transaction?.id}
            onClick={() => handleTransactionClick(transaction)}
            className="p-4 hover:bg-muted/30 cursor-pointer transition-colors duration-150"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-muted/50 rounded-lg flex items-center justify-center">
                  <Icon name={getTransactionIcon(transaction?.type)} size={18} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{transaction?.description}</p>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-sm text-muted-foreground">{transaction?.id}</span>
                    <span className="text-sm text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">{transaction?.category}</span>
                    <span className="text-sm text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">
                      {new Date(transaction?.date)?.toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <p className={`font-semibold ${getAmountColor(transaction?.amount)}`}>
                  {transaction?.amount > 0 ? '+' : ''}${Math.abs(transaction?.amount)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction?.status)}`}>
                  {transaction?.status?.charAt(0)?.toUpperCase() + transaction?.status?.slice(1)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Recent Transactions</h2>
          <div className="flex items-center space-x-2">
            <div className="flex bg-muted/50 rounded-lg p-1">
              {['all', 'invoice', 'expense', 'payment']?.map((filterType) => (
                <button
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-150 capitalize ${
                    filter === filterType 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {filterType}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/banking')}>
              View All
              <Icon name="ArrowRight" size={14} />
            </Button>
          </div>
        </div>
      </div>
      
      {renderContent()}
      
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {loading ? 'Loading...' : error ? 'Connection error occurred' : 
             `Showing ${filteredTransactions?.length} of ${displayTransactions?.length} transactions`}
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate('/banking')}>
            <Icon name="ExternalLink" size={14} />
            View in Banking
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecentTransactions;